import Clickable from '@perpay-web/components/base/Clickable/Clickable';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './Alert.scss';

export const AlertIcons = {
    EXCLAMATION: 'exclamation',
    DOUBLE_EXCLAMATION: 'double-exclamation',
    GIFT: 'gift',
    PACKAGE: 'package',
    CREDIT_CARD_GREEN: 'credit-card-green',
    DONE_MARK: 'done-mark',
    ENVELOPE: 'envelope',
    PERSON_LIFTING_WEIGHTS: 'person-lifting-weights',
    CHART_INCREASING: 'chart-increasing',
    PARTY_FACE: 'party-face',
    SMILING_FACE_WITH_SUNGLASSES: 'smiling-face-with-sunglasses',
    POLICE_CAR_LIGHT: 'police-car-light',
    STOP_SIGN: 'stop-sign',
    MAGNIFYING_GLASS_TILTED_RIGHT: 'magnifying-glass-tilted-right',
    PAGE_FACING_UP: 'page-facing-up',
    FROZEN_FACE: 'frozen-face',
};

export const AlertSeverity = {
    INFO: 'info',
    SUCCESS: 'success',
    WARNING: 'warning',
    DANGER: 'danger',
    CLOSED: 'closed',
};

/**
 * Alert component for displaying status messages with an icon and optional interactivity.
 *
 * @param {Object} props
 * @param {typeof AlertIcons[keyof typeof AlertIcons]} [props.icon] - Icon type from AlertIcons
 * @param {typeof AlertSeverity[keyof typeof AlertSeverity]} props.severity - Severity level from AlertSeverity
 * @param {React.ReactNode} [props.content=null] - Optional content to be displayed inside the alert
 * @param {string} [props.className=''] - Optional className
 * @param {boolean} [props.isRightArrow=false] - Optional right arrow icon display
 * @param {function} [props.onClick] - Optional click handler
 *
 * @returns {JSX.Element} Alert component
 */

const Alert = ({
    icon,
    severity,
    content = null,
    className = '',
    isRightArrow = false,
    onClick,
}) => {
    if (!content) {
        return null;
    }

    const renderIcon = Object.values(AlertIcons).includes(icon) ? (
        <div className={styles[`icon__${icon}`]} />
    ) : null;
    const severityClass = severity.length
        ? styles[`alert--${severity}`]
        : styles[`alert--info`];

    const isClickable = !!onClick;

    const body = (
        <div className={styles.alert__container}>
            <div className={styles.content}>
                <div className={styles.icon}>{renderIcon}</div>
                <div className={styles.content__message}>
                    <span className={styles.content__copy}>{content}</span>
                </div>
            </div>
            {isRightArrow ? (
                <div className={styles['icon__make-action']} />
            ) : null}
        </div>
    );

    const containerClassName = getClassName(
        styles.alert,
        severityClass,
        className,
    );

    if (isClickable) {
        return (
            <Clickable className={containerClassName} onClick={onClick}>
                {body}
            </Clickable>
        );
    }

    return <div className={containerClassName}>{body}</div>;
};

export default Alert;
